import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { insightsAPI, vehiclesAPI, clientsAPI } from '../services/api';
import { formatDateBritish } from '../utils/dateUtils';
import ScheduledReportsTab from '../components/ScheduledReportsTab';
import './VehicleList.css';

interface Client {
  id: number;
  companyName: string;
}
interface PolicyPeriod {
  id: number;
  periodNumber: number;
  policyStartDate: string;
  policyEndDate: string;
  status: 'Current' | 'Archived' | 'Scheduled';
}

interface VehicleReport {
  clientName: string;
  registration: string;
  siteLocation: string;
  value: string;
  grossVehicleWeightKg?: number | null;
  status: string;
  isActive: boolean;
  inceptionDate: string | null;
  endDate: string | null;
  onCoverDate: string | null;
  onCoverBy: string | null;
  offCoverDate: string | null;
  coverType?: string;
  changes: Array<{
    changeType: string;
    changeDate: string;
    changeTime: string;
    processedDate: string | null;
    processedTime: string | null;
    processedBy: string | null;
    changedBy: string;
    notes: string | null;
  }>;
}

interface CustomReportCategories {
  pendingCover: boolean;
  onCover: boolean;
  tempAdditions: boolean;
  removalRequests: boolean;
  changeOfVehicle: boolean;
}

interface CustomReportData {
  pendingCover:    any[] | null;
  onCover:         any[] | null;
  tempAdditions:   any[] | null;
  removalRequests: any[] | null;
  changeOfVehicle: any[] | null;
}

const CATEGORY_META: Record<keyof CustomReportCategories, { label: string; colour: string; description: string }> = {
  pendingCover:    { label: 'Vehicles Pending Cover',        colour: '#f59e0b', description: 'Added by client — awaiting approval' },
  onCover:         { label: 'Vehicle Additions',             colour: '#10b981', description: 'Approved permanent additions during the selected range' },
  tempAdditions:   { label: 'Temp Vehicle Additions',        colour: '#3b82f6', description: 'Temporary cover requests by client' },
  removalRequests: { label: 'Vehicle Removal Requests',      colour: '#ef4444', description: 'Removal requests submitted by client' },
  changeOfVehicle: { label: 'Change of Vehicle Requests',    colour: '#8b5cf6', description: 'Vehicle swap requests by client' },
};

// Calculates total cover days between vehicle inception date and client policy end date.
// Uses direct string-splitting for YYYY-MM-DD values to avoid UTC midnight timezone shift.
const calcDaysOnCover = (inception: string | null, end: string | null): string => {
  if (!inception || !end) return '—';
  const parseLocal = (s: string) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(s);
  };

  const s = parseLocal(inception);
  const e = parseLocal(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return '—';
  const days = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
  return days > 0 ? `${days} days` : '—';
};

const statusColour = (status: string) => {
  if (status === 'Active')        return '#10b981';
  if (status === 'Approved')      return '#10b981';
  if (status === 'Cover Pending') return '#f59e0b';
  if (status === 'Pending Cover') return '#f59e0b';
  if (status === 'Pending')       return '#f59e0b';
  if (status === 'Inactive')      return '#6b7280';
  if (status === 'Rejected')      return '#ef4444';
  return '#374151';
};

const InsightsReporting: React.FC = () => {
  // Shared state
  const [clients, setClients]           = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [policyPeriods, setPolicyPeriods] = useState<PolicyPeriod[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('current');
  const [dateRange, setDateRange]       = useState<string>('all');
  const [startDate, setStartDate]       = useState<string>('');
  const [endDate, setEndDate]           = useState<string>('');
const [activeTab, setActiveTab]       = useState<'vehicleChanges' | 'customReport' | 'fleetStatus' | 'scheduledReports'>('vehicleChanges');
  const [fsSubTab, setFsSubTab]         = useState<'current' | 'removed' | 'blended'>('blended');


  // Vehicle Changes Report
  const [reports, setReports]           = useState<VehicleReport[]>([]);
  const [vcLoading, setVcLoading]       = useState(false);

  // Fleet Status — loaded directly from vehicles table (independent of change-log history)
  const [fleetStatusVehicles, setFleetStatusVehicles] = useState<VehicleReport[]>([]);
  const [fleetReconciliation, setFleetReconciliation] = useState<{ openingCount: number; additions: any[]; removals: any[]; closingFleet: any[] } | null>(null);
  const [openingBaseline, setOpeningBaseline] = useState('');
  const [fsLoading, setFsLoading]       = useState(false);
  const [fsBackfilling, setFsBackfilling] = useState(false);

  // Custom Report
  const [categories, setCategories]     = useState<CustomReportCategories>({
    pendingCover:    false,
    onCover:         false,
    tempAdditions:   false,
    removalRequests: false,
    changeOfVehicle: false,
  });
  const [customData, setCustomData]     = useState<CustomReportData | null>(null);
  const [excludedRequesters, setExcludedRequesters] = useState('');
  const [crLoading, setCrLoading]       = useState(false);

  useEffect(() => { loadClients(); }, []);
  useEffect(() => {
    setPolicyPeriods([]);
    setSelectedPeriod('current');
    if (selectedClient === 'all') return;
    clientsAPI.getPolicyPeriods(parseInt(selectedClient))
      .then(response => setPolicyPeriods(Array.isArray(response.data) ? response.data : []))
      .catch(() => {});
  }, [selectedClient]);

  const loadClients = async () => {
    try {
      const response = await insightsAPI.getClientsList();
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const backfillFleetWeights = async () => {
    if (selectedClient === 'all') {
      alert('Please select a specific client before refreshing vehicle weights.');
      return;
    }

    setFsBackfilling(true);
    try {
      const response = await vehiclesAPI.backfillGrossWeights(parseInt(selectedClient));
      const { checkedCount, updatedCount, unavailableCount } = response.data;
      alert(`Checked ${checkedCount} vehicle(s). Added gross weights for ${updatedCount}${unavailableCount ? `; ${unavailableCount} were unavailable from UKVD.` : '.'}`);
      await loadFleetStatusDirect();
    } catch (error: any) {
      console.error('Error backfilling vehicle weights:', error);
      alert(error.response?.data?.message || 'Failed to refresh vehicle weights.');
    } finally {
      setFsBackfilling(false);
    }
  };

  const calculateDateRange = () => {
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null   = null;
    switch (dateRange) {
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end   = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'previousQuarter': {
        const cq  = Math.floor(now.getMonth() / 3);
        const pq  = cq === 0 ? 3 : cq - 1;
        const yr  = cq === 0 ? now.getFullYear() - 1 : now.getFullYear();
        start = new Date(yr, pq * 3, 1);
        end   = new Date(yr, (pq + 1) * 3, 0);
        break;
      }
      case 'custom':
        if (startDate) start = new Date(startDate);
        if (endDate)   end   = new Date(endDate);
        break;
    }
    return { start, end };
  };

  // ── Vehicle Changes Report ────────────────────────────────────────────
  const loadVehicleChanges = async () => {
    setVcLoading(true);
    try {
      const { start, end } = calculateDateRange();
      const clientId = selectedClient === 'all' ? undefined : parseInt(selectedClient);
      const response = await insightsAPI.getVehicleChanges(clientId, start, end);
      setReports(response.data);
    } catch (error) {
      console.error('Error loading vehicle changes:', error);
      alert('Failed to load Vehicle Changes Report');
    } finally {
      setVcLoading(false);
    }
  };

  const exportVehicleChangesToExcel = () => {
    const rows: any[] = [];
    reports.forEach((r) => {
      const base = {
        'Client Name':       r.clientName,
        'Registration':      r.registration,
        'Site/Location':     r.siteLocation,
        'Value':             r.value,
        'Gross Vehicle Weight (kg)': r.grossVehicleWeightKg ?? '',
        'Cover Status':      r.status === 'Active' ? 'On Cover' : r.status === 'Cover Pending' ? 'Cover Pending' : r.status,
        'Cover Type':        r.coverType || (r.changes.some(c => c.changeType === 'Temporary Cover') ? 'Temporary' : 'Permanent'),
        'Policy Start Date': r.inceptionDate ? formatDateBritish(r.inceptionDate) : '',
        'Cover End Date':    r.endDate       ? formatDateBritish(r.endDate)       : '',
        'Days on Cover':     calcDaysOnCover(r.inceptionDate, r.endDate),
        'Approved By':       r.onCoverBy     ?? '',
        'Off Cover Date':    r.offCoverDate  ? formatDateBritish(r.offCoverDate)  : '',
      };
      if (r.changes.length === 0) {
        rows.push({ ...base, 'Event': '', 'Event Date': '', 'Event Time': '', 'Requested By': '', 'Processed By': '', 'Processed Date': '' });
      } else {
        r.changes.forEach((c) => rows.push({
          ...base,
          'Event':          c.changeType,
          'Event Date':     formatDateBritish(c.changeDate),
          'Event Time':     c.changeTime,
          'Requested By':   c.changedBy,
          'Processed By':   c.processedBy    ?? '',
          'Processed Date': c.processedDate  ? formatDateBritish(c.processedDate) : '',
        }));
      }
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Vehicle Changes');
    XLSX.writeFile(wb, `Vehicle_Changes_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ── Fleet Status — direct vehicle load ─────────────────────────────────
  // The vehicle-changes endpoint skips vehicles that only have bulk-upload
  // change logs (e.g. Delifresh added by CSV today).  Fleet Status needs the
  // raw live vehicle list, so it calls vehiclesAPI.getByClient directly.
  const loadFleetStatusDirect = async () => {
    if (selectedClient === 'all') {
      alert('Please select a specific client to generate the Fleet Status report.');
      return;
    }
    setFsLoading(true);
    try {
      const clientIdNum = parseInt(selectedClient);
      if (dateRange === 'custom' && startDate && endDate) {
        const archivedPeriod = policyPeriods.find(period => String(period.id) === selectedPeriod && period.status === 'Archived');
        const response = await insightsAPI.getFleetReconciliation(clientIdNum, startDate, endDate, archivedPeriod?.id);
        const reconciliation = response.data;
        setFleetReconciliation(reconciliation);
        setOpeningBaseline(String(reconciliation.openingCount ?? ''));
        setFleetStatusVehicles((reconciliation.closingFleet || []).map((v: any) => ({
          clientName: reconciliation.clientName || 'Unknown',
          registration: v.registration,
          siteLocation: v.siteLocation,
          value: v.vehicleValue?.toString() ?? '0',
          grossVehicleWeightKg: v.grossVehicleWeightKg ?? null,
          status: 'Active',
          isActive: true,
          inceptionDate: v.eventAt ?? null,
          endDate: endDate,
          onCoverDate: v.eventAt ?? null,
          onCoverBy: v.actor ?? null,
          offCoverDate: null,
          coverType: 'Permanent',
          changes: [],
        })));
        return;
      }
      setFleetReconciliation(null);
      const archivedPeriod = policyPeriods.find(period => String(period.id) === selectedPeriod && period.status === 'Archived');
      if (archivedPeriod) {
        const [snapshotRes, clientRes] = await Promise.all([
          clientsAPI.getPolicyPeriodSnapshot(clientIdNum, archivedPeriod.id),
          clientsAPI.getById(clientIdNum),
        ]);
        const snapshot = snapshotRes.data;
        const snapPeriod = snapshot.period || {};
        setFleetStatusVehicles((snapshot.vehicles || []).map((v: any) => ({
          clientName: clientRes.data.companyName || 'Unknown',
          registration: v.registration ?? v.Registration,
          siteLocation: v.siteName ?? v.SiteName ?? (clientRes.data.city ? `Head Office (${clientRes.data.city})` : 'Head Office'),
          value: (v.vehicleValue ?? v.VehicleValue)?.toString() ?? '0',
          grossVehicleWeightKg: v.grossVehicleWeightKg ?? v.GrossVehicleWeightKg ?? null,
          status: v.coverStatus ?? v.CoverStatus ?? 'Inactive',
          isActive: v.isActive ?? v.IsActive,
          inceptionDate: v.inceptionDate ?? v.InceptionDate ?? null,
          endDate: snapPeriod.policyEndDate ?? snapPeriod.PolicyEndDate ?? archivedPeriod.policyEndDate,
          onCoverDate: v.coverEffectiveDate ?? v.CoverEffectiveDate ?? null,
          onCoverBy: v.updatedBy ?? v.UpdatedBy ?? null,
          offCoverDate: null,
          coverType: (snapPeriod.coverType ?? snapPeriod.CoverType) || 'Permanent',
          changes: [],
        })));
        return;
      }
      const [vehiclesRes, clientRes] = await Promise.all([
        vehiclesAPI.getByClient(clientIdNum),
        clientsAPI.getById(clientIdNum),
      ]);
      const cd = clientRes.data;
      const policyEndDate: string | null = cd.policyEndDate ?? null;
      // Exclude bulk-upload-undone phantom vehicles: they have IsActive=false but
      // CoverStatus is not 'Inactive' (they were never approved, just uploaded then undone).
      // Genuine removals always have CoverStatus='Inactive' set by the approve-removal endpoint.
      const realVehicles = (vehiclesRes.data as any[]).filter(
        (v: any) => v.isActive || v.coverStatus === 'Inactive'
      );
      const mapped: VehicleReport[] = realVehicles.map((v: any) => ({
        clientName:   cd.companyName || 'Unknown',
        registration: v.registration,
        siteLocation: v.site?.name || (cd.city ? `Head Office (${cd.city})` : 'Head Office'),
        value:        v.vehicleValue?.toString() ?? '0',
        grossVehicleWeightKg: v.grossVehicleWeightKg ?? null,
        status:       v.coverStatus  ?? 'Inactive',
        isActive:     v.isActive,
        inceptionDate:  v.inceptionDate    ?? null,
        endDate:        policyEndDate,
        onCoverDate:    v.coverEffectiveDate ?? null,
        onCoverBy:      v.updatedBy         ?? null,
        offCoverDate:   null,
        coverType:      (v as any).tempCoverInfo ? 'Temporary' : 'Permanent',
        changes:        [],
      }));
      setFleetStatusVehicles(mapped);
    } catch (error) {
      console.error('Error loading fleet status:', error);
      alert('Failed to load Fleet Status');
    } finally {
      setFsLoading(false);
    }
  };

  const saveOpeningBaseline = async () => {
    if (selectedClient === 'all' || !startDate || !openingBaseline) return;
    const period = policyPeriods.find(p => String(p.id) === selectedPeriod && p.status === 'Archived');
    try {
      await insightsAPI.saveFleetBaseline({ clientId: parseInt(selectedClient), policyPeriodId: period?.id, asOfDate: startDate, openingFleetCount: Number(openingBaseline) });
      await loadFleetStatusDirect();
    } catch {
      alert('Unable to save the opening fleet baseline.');
    }
  };

  // ── Custom Report ─────────────────────────────────────────────────────
  const allSelected = Object.values(categories).every(Boolean);
  const noneSelected = Object.values(categories).every((v) => !v);

  const toggleAll = () => {
    const next = !allSelected;
    setCategories({ pendingCover: next, onCover: next, tempAdditions: next, removalRequests: next, changeOfVehicle: next });
  };

  const loadCustomReport = async () => {
    if (noneSelected) { alert('Please select at least one category.'); return; }
    setCrLoading(true);
    try {
      const { start, end } = calculateDateRange();
      const clientId = selectedClient === 'all' ? undefined : parseInt(selectedClient);
      const response = await insightsAPI.getCustomReport({
        clientId,
        startDate: start,
        endDate:   end,
        includePendingCover:    categories.pendingCover,
        includeOnCover:         categories.onCover,
        includeTempAdditions:   categories.tempAdditions,
        includeRemovalRequests: categories.removalRequests,
        includeChangeOfVehicle: categories.changeOfVehicle,
      });
      const excluded = excludedRequesters.split(',').map(value => value.trim().toLowerCase()).filter(Boolean);
      const filterRequesters = (items: any[] | null) => !items ? items : items.filter(item =>
        !excluded.includes((item.requestedBy || '').trim().toLowerCase()));
      setCustomData({
        ...response.data,
        onCover: filterRequesters(response.data.onCover),
        tempAdditions: filterRequesters(response.data.tempAdditions),
        removalRequests: filterRequesters(response.data.removalRequests),
        changeOfVehicle: filterRequesters(response.data.changeOfVehicle),
      });
    } catch (error) {
      console.error('Error loading custom report:', error);
      alert('Failed to load Custom Report');
    } finally {
      setCrLoading(false);
    }
  };

  const exportCustomReportToExcel = () => {
    if (!customData) return;
    const wb = XLSX.utils.book_new();

    const addSheet = (data: any[] | null, sheetName: string, columns: string[]) => {
      if (!data || data.length === 0) return;
      const rows = data.map((item) => {
        const row: Record<string, any> = {};
        columns.forEach((col) => { row[col] = (item as any)[toCamelCase(col)] ?? ''; });
        return row;
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheetName);
    };

    addSheet(customData.pendingCover,    'Pending Cover',     ['Client Name', 'Registration', 'Make', 'Model', 'Vehicle Type', 'Gross Vehicle Weight Kg', 'Site Location', 'Vehicle Value', 'Requested At', 'Requested By', 'Status']);
    // On Cover: manual mapping — removes Cover Effective Date, renames Inception Date → Policy Start Date
    if (customData.onCover && customData.onCover.length > 0) {
      const onCoverRows = customData.onCover.map((item: any) => ({
        'Client Name':       item.clientName    ?? '',
        'Registration':      item.registration  ?? '',
        'Make':              item.make          ?? '',
        'Model':             item.model         ?? '',
        'Vehicle Type':      item.vehicleType   ?? '',
        'Site Location':     item.siteLocation  ?? '',
        'Vehicle Value':     item.vehicleValue  ?? '',
        'Gross Vehicle Weight (kg)': item.grossVehicleWeightKg ?? '',
        'Approved At':       item.approvedAt    ?? '',
        'Approved By':       item.approvedBy    ?? '',
        'Policy Start Date': item.inceptionDate ? formatDateBritish(item.inceptionDate) : '',
        'Cover Type':        item.isTempCover   ? 'Temporary' : 'Permanent',
        'Status':            item.status        ?? '',
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(onCoverRows), 'On Cover');
    }
    addSheet(customData.tempAdditions,   'Temp Additions',    ['Client Name', 'Registration', 'Make', 'Model', 'Vehicle Type', 'Gross Vehicle Weight Kg', 'Site Location', 'Vehicle Value', 'Requested At', 'Requested By', 'Cover Start Date', 'Cover End Date', 'Status', 'Processed At', 'Processed By']);
    addSheet(customData.removalRequests, 'Removal Requests',  ['Client Name', 'Registration', 'Make', 'Model', 'Vehicle Type', 'Gross Vehicle Weight Kg', 'Site Location', 'Requested At', 'Requested By', 'Requested Cover End Date', 'Status', 'Processed At', 'Processed By']);
    addSheet(customData.changeOfVehicle, 'Change of Vehicle', ['Client Name', 'Old Registration', 'Old Make', 'Old Model', 'New Registration', 'New Make', 'New Model', 'New Vehicle Type', 'New Vehicle Value', 'Site Location', 'Requested At', 'Requested By', 'Cover Start Date', 'Status', 'Processed At', 'Processed By']);

    XLSX.writeFile(wb, `Custom_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const toCamelCase = (col: string) =>
    col.split(' ').map((w, i) => i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)).join('');

  // ── Fleet Status (blended) ─────────────────────────────────────────
  const fsCurrentFleet   = fleetStatusVehicles.filter((r) => r.isActive !== false && r.status !== 'Inactive');
  const fsRemovedFleet   = fleetStatusVehicles.filter((r) => !r.isActive || r.status === 'Inactive');
  const fsBlendedFleet   = fleetStatusVehicles;
  const fsKnownWeightKg = fsBlendedFleet.reduce((total, vehicle) => total + (vehicle.grossVehicleWeightKg ?? 0), 0);
  const fsMissingWeightCount = fsBlendedFleet.filter(vehicle => vehicle.grossVehicleWeightKg == null).length;

  const fsActiveRows = fsSubTab === 'current' ? fsCurrentFleet
    : fsSubTab === 'removed' ? fsRemovedFleet
    : fsBlendedFleet;

  // Excel sheet names must be ≤31 chars and must not contain: \ / ? * [ ] :
  const sanitiseSheetName = (name: string) =>
    name.replace(/[\\/\?\*\[\]\:]/g, '-').slice(0, 31);

  const exportFleetStatusToExcel = () => {
    if (fleetReconciliation) {
      const reconciliationRows = (records: any[]) => records.map((r) => ({
        'Registration': r.registration,
        'Make': r.make ?? '',
        'Model': r.model ?? '',
        'Site/Location': r.siteLocation ?? '',
        'Vehicle Value': r.vehicleValue ? `£${r.vehicleValue}` : '',
        'Gross Vehicle Weight (kg)': r.grossVehicleWeightKg ?? '',
        'Movement Date': r.eventAt ? formatDateBritish(r.eventAt) : '',
        'Processed By': r.actor ?? '',
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(reconciliationRows(fleetReconciliation.additions)), 'Additions');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(reconciliationRows(fleetReconciliation.removals)), 'Removals');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(reconciliationRows(fleetReconciliation.closingFleet)), 'Closing Live Fleet');
      XLSX.writeFile(wb, `Fleet_Reconciliation_${startDate}_to_${endDate}.xlsx`);
      return;
    }
    const rows = fsActiveRows.map((r) => ({
      'Client':            r.clientName,
      'Registration':      r.registration,
      'Site/Location':     r.siteLocation,
      'Value':             r.value ? `\u00a3${r.value}` : '',
      'Gross Vehicle Weight (kg)': r.grossVehicleWeightKg ?? '',
      'Cover Status':      r.status === 'Active' ? 'On Cover' : r.status,
      'Cover Type':        r.coverType || 'Permanent',
      'Policy Start Date': r.inceptionDate ? formatDateBritish(r.inceptionDate) : '',
      'Cover End Date':    r.endDate       ? formatDateBritish(r.endDate)       : '',
      'Days on Cover':     calcDaysOnCover(r.inceptionDate, r.endDate),
      'Approved By':       r.onCoverBy     ?? '',
      'Off Cover Date':    r.offCoverDate  ? formatDateBritish(r.offCoverDate)  : '',
    }));
    const wb = XLSX.utils.book_new();
    const rawSheetName = fsSubTab === 'current' ? 'Live on Cover Fleet' : fsSubTab === 'removed' ? 'Removed Vehicles' : 'Full Policy Vehicle Transactions';
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sanitiseSheetName(rawSheetName));
    XLSX.writeFile(wb, `Fleet_Status_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const totalCustomResults = !customData ? 0 :
    (customData.pendingCover?.length ?? 0) +
    (customData.onCover?.length ?? 0) +
    (customData.tempAdditions?.length ?? 0) +
    (customData.removalRequests?.length ?? 0) +
    (customData.changeOfVehicle?.length ?? 0);

  // A report needs at least a specific client OR a date range — "All clients + All Time"
  // scans the entire database and will time out.
  const hasFilter = selectedClient !== 'all' || dateRange !== 'all';
  const isArchivedPeriodSelected = policyPeriods.some(period => String(period.id) === selectedPeriod && period.status === 'Archived');

  // ── Shared filter UI ──────────────────────────────────────────────────
  const renderFilters = () => (
    <div className="card" style={{ marginBottom: '24px' }}>
      <h3 style={{ marginBottom: '20px' }}>Filters</h3>
      <div className="form-row">
        <div className="form-group">
          <label>Client</label>
          <select value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)}>
            <option value="all">All Clients</option>
            {clients.map((c) => (<option key={c.id} value={c.id}>{c.companyName}</option>))}
          </select>
        </div>
        {selectedClient !== 'all' && (
          <div className="form-group">
            <label>Policy Period</label>
            <select value={selectedPeriod} onChange={(e) => { setSelectedPeriod(e.target.value); setFleetStatusVehicles([]); }}>
              <option value="current">Current policy period</option>
              {policyPeriods.filter(period => period.status === 'Archived').map(period => (
                <option key={period.id} value={period.id}>Policy Period {period.periodNumber} ({formatDateBritish(period.policyStartDate)} – {formatDateBritish(period.policyEndDate)}) — archived</option>
              ))}
            </select>
          </div>
        )}
        <div className="form-group">
          <label>Date Range</label>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="all">All Time</option>
            <option value="lastMonth">Last Month</option>
            <option value="previousQuarter">Previous Quarter</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
      </div>
      {dateRange === 'custom' && (
        <div className="form-row">
          <div className="form-group">
              <label htmlFor="report-start-date">Start Date</label>
              <input id="report-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="form-group">
              <label htmlFor="report-end-date">End Date</label>
              <input id="report-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
      )}
      {isArchivedPeriodSelected && (
        <div style={{ marginTop: 12, padding: '10px 14px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, fontSize: 13, color: '#5b21b6' }}>
          Archived periods are immutable evidence snapshots in <strong>Fleet Status</strong>. Vehicle Changes and Custom Report use live-record history for the selected client and date range.
        </div>
      )}
      {!hasFilter && (
        <div style={{ marginTop: 12, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
          ⚠️ Please select a specific <strong>client</strong> or a <strong>date range</strong> before generating a report. Querying all clients across all time may time out.
        </div>
      )}
    </div>
  );

  // ── Custom report category section ────────────────────────────────────
  const renderCustomCategory = (
    key: keyof CustomReportCategories,
    data: any[] | null,
    renderTable: (rows: any[]) => React.ReactElement
  ) => {
    if (!categories[key] || !data) return null;
    const meta = CATEGORY_META[key];
    return (
      <div key={key} className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: meta.colour, display: 'inline-block' }} />
          <h3 style={{ margin: 0 }}>{meta.label}</h3>
          <span style={{ color: '#6b7280', fontSize: '13px' }}>{meta.description}</span>
          <span style={{ marginLeft: 'auto', background: meta.colour + '22', color: meta.colour, padding: '2px 10px', borderRadius: 12, fontSize: '13px', fontWeight: 600 }}>
            {data.length} record{data.length !== 1 ? 's' : ''}
          </span>
        </div>
        {data.length === 0
          ? <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No records found for the selected filters.</p>
          : <div style={{ overflowX: 'auto' }}>{renderTable(data)}</div>}
      </div>
    );
  };

  const TableHeader: React.FC<{ cols: string[] }> = ({ cols }) => (
    <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
  );

  const fmtDT = (val: string | null) => val ? `${formatDateBritish(val)} ${new Date(val).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '—';

  return (
    <div className="vehicle-list">
      <div className="page-header">
        <h1>Insights &amp; Reporting</h1>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: '24px', borderBottom: '2px solid #e5e7eb' }}>
{([
          { key: 'vehicleChanges', label: '\uD83D\uDCCB Vehicle Changes' },
          { key: 'customReport',   label: '\uD83D\uDD0D Custom Report' },
          { key: 'fleetStatus',    label: '\uD83D\uDE9A Fleet Status' },
          { key: 'scheduledReports', label: '\uD83D\uDCE7 Scheduled Reports' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              padding: '10px 28px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '14px',
              color: activeTab === key ? '#1e40af' : '#6b7280',
              borderBottom: activeTab === key ? '2px solid #1e40af' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.15s',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ──────────────── Vehicle Changes Report tab ──────────────── */}
      {activeTab === 'vehicleChanges' && (
        <>
          {renderFilters()}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <button className="btn btn-primary" onClick={loadVehicleChanges} disabled={vcLoading || !hasFilter}>
              {vcLoading ? 'Loading...' : 'Generate Report'}
            </button>
            {reports.length > 0 && (
              <button className="btn btn-secondary" onClick={exportVehicleChangesToExcel}>📥 Export to Excel</button>
            )}
          </div>

          {reports.length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: '20px' }}>Vehicle Changes Report ({reports.length} vehicles)</h3>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Registration</th>
                      <th>Site/Location</th>
                      <th>Value</th>
                      <th>Gross Vehicle Weight</th>
                      <th>Status</th>
                      <th>Cover Type</th>
                      <th>Policy Start Date</th>
                      <th>Cover End Date</th>
                      <th style={{ textAlign: 'center' }}>Days on Cover</th>
                      <th>Approved By</th>
                      <th>Off Cover Date</th>
                      <th>Events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((r, i) => (
                      <tr key={i}>
                        <td><strong>{r.clientName}</strong></td>
                        <td>{r.registration}</td>
                        <td>{r.siteLocation}</td>
                        <td>£{r.value}</td>
                        <td>{r.grossVehicleWeightKg != null ? `${r.grossVehicleWeightKg.toLocaleString()} kg` : '—'}</td>
                        <td>
                          <span style={{ color: statusColour(r.status), fontWeight: 600 }}>
                            {r.status === 'Active' ? 'On Cover' : r.status === 'Cover Pending' ? 'Cover Pending' : r.status}
                          </span>
                        </td>
                        <td>{r.coverType || (r.changes.some(c => c.changeType === 'Temporary Cover') ? 'Temporary' : 'Permanent')}</td>
                        <td>{r.inceptionDate ? formatDateBritish(r.inceptionDate) : '\u2014'}</td>
                        <td>{r.endDate        ? formatDateBritish(r.endDate)        : '\u2014'}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>{calcDaysOnCover(r.inceptionDate, r.endDate)}</td>
                        <td>{r.onCoverBy ?? '—'}</td>
                        <td>{r.offCoverDate ? formatDateBritish(r.offCoverDate) : '—'}</td>
                        <td>
                          {r.changes.length > 0 ? (
                            <div style={{ fontSize: '12px' }}>
                              {r.changes.map((c, ci) => (
                                <div key={ci} style={{ marginBottom: ci < r.changes.length - 1 ? '8px' : 0, paddingBottom: ci < r.changes.length - 1 ? '8px' : 0, borderBottom: ci < r.changes.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                  <strong>{c.changeType}</strong><br />
                                  {formatDateBritish(c.changeDate)} {c.changeTime}<br />
                                  <span style={{ color: '#6b7280' }}>By: {c.changedBy}</span>
                                  {c.processedDate && (<><br /><span style={{ color: '#10b981' }}>Processed: {formatDateBritish(c.processedDate)}{c.processedBy ? ` by ${c.processedBy}` : ''}</span></>)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ──────────────── Fleet Status tab ──────────────── */}
      {activeTab === 'fleetStatus' && (
        <>
          {renderFilters()}
          {selectedClient === 'all' && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
              ⚠️ Fleet Status requires a <strong>specific client</strong> to be selected — it loads live vehicle data directly.
            </div>
          )}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <button className="btn btn-primary" onClick={loadFleetStatusDirect} disabled={fsLoading || selectedClient === 'all'}>
              {fsLoading ? 'Loading...' : 'Generate Fleet Status'}
            </button>
            {fleetStatusVehicles.length > 0 && (
              <button className="btn btn-secondary" onClick={exportFleetStatusToExcel}>📥 Export to Excel</button>
            )}
          </div>

          {fleetStatusVehicles.length > 0 && (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                {fleetReconciliation && (
                  <>
                    <div className="card" style={{ margin: 0, padding: '12px 16px', minWidth: 245 }}><div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Confirmed opening fleet</div><div style={{ display: 'flex', gap: 8 }}><input aria-label="Confirmed opening fleet" type="number" min="0" value={openingBaseline} onChange={e => setOpeningBaseline(e.target.value)} style={{ width: 80 }} /><button className="btn btn-secondary" onClick={saveOpeningBaseline}>Save</button></div></div>
                    <div className="card" style={{ margin: 0, padding: '12px 16px', minWidth: 170 }}><div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Opening live fleet</div><div style={{ fontSize: 20, color: '#1e40af', fontWeight: 800 }}>{fleetReconciliation.openingCount}</div></div>
                    <div className="card" style={{ margin: 0, padding: '12px 16px', minWidth: 170 }}><div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Additions</div><div style={{ fontSize: 20, color: '#15803d', fontWeight: 800 }}>+{fleetReconciliation.additions.length}</div></div>
                    <div className="card" style={{ margin: 0, padding: '12px 16px', minWidth: 170 }}><div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Removals</div><div style={{ fontSize: 20, color: '#b91c1c', fontWeight: 800 }}>-{fleetReconciliation.removals.length}</div></div>
                    <div className="card" style={{ margin: 0, padding: '12px 16px', minWidth: 170 }}><div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Closing live fleet</div><div style={{ fontSize: 20, color: '#1e40af', fontWeight: 800 }}>{fleetReconciliation.closingFleet.length}</div></div>
                  </>
                )}
                {!fleetReconciliation && <><div className="card" style={{ margin: 0, padding: '12px 16px', minWidth: 190 }}><div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Known gross fleet weight</div><div style={{ fontSize: 20, color: '#1e40af', fontWeight: 800 }}>{fsKnownWeightKg.toLocaleString()} kg</div></div>
                <div className="card" style={{ margin: 0, padding: '12px 16px', minWidth: 190 }}><div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Vehicles without weight</div><div style={{ fontSize: 20, color: fsMissingWeightCount ? '#b45309' : '#15803d', fontWeight: 800 }}>{fsMissingWeightCount}</div></div></>}
              </div>
              {fleetReconciliation && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3 style={{ marginBottom: 16 }}>Quarterly movements</h3>
                  <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>Bulk Upload and Undo Upload activity is excluded. Closing fleet = opening fleet + additions − removals.</p>
                  <div style={{ overflowX: 'auto' }}>
                    <table><thead><tr><th>Movement</th><th>Date</th><th>Registration</th><th>Site</th><th>Processed by</th></tr></thead>
                      <tbody>{[...fleetReconciliation.additions, ...fleetReconciliation.removals].sort((a, b) => new Date(a.eventAt).getTime() - new Date(b.eventAt).getTime()).map((m, i) => (
                        <tr key={`${m.eventType}-${m.id}-${i}`}><td><strong style={{ color: m.eventType === 'Added' ? '#15803d' : '#b91c1c' }}>{m.eventType}</strong></td><td>{formatDateBritish(m.eventAt)}</td><td>{m.registration}</td><td>{m.siteLocation}</td><td>{m.actor || '—'}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Sub-tabs */}
              <div style={{ display: 'flex', gap: 0, marginBottom: '20px', borderBottom: '2px solid #e5e7eb' }}>
                {([
                  { key: 'blended',  label: fleetReconciliation ? '📋 Closing Live Fleet' : '📋 Full Policy Vehicle Transactions List', count: fsBlendedFleet.length },
                  { key: 'current',  label: '✅ Live on Cover Fleet', count: fsCurrentFleet.length },
                  { key: 'removed',  label: fleetReconciliation ? '⛔ In-period removals shown above' : '⛔ Removed / Off Cover', count: fsRemovedFleet.length },
                ] as const).map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => setFsSubTab(key)}
                    style={{
                      padding: '8px 22px', border: 'none', background: 'none', cursor: 'pointer',
                      fontWeight: 600, fontSize: '13px',
                      color: fsSubTab === key ? '#1e40af' : '#6b7280',
                      borderBottom: fsSubTab === key ? '2px solid #1e40af' : '2px solid transparent',
                      marginBottom: '-2px', transition: 'all 0.15s',
                    }}
                  >
                    {label}
                    <span style={{ marginLeft: 6, background: fsSubTab === key ? '#dbeafe' : '#f3f4f6', color: fsSubTab === key ? '#1e40af' : '#6b7280', borderRadius: 12, padding: '1px 7px', fontSize: 12 }}>
                      {count}
                    </span>
                  </button>
                ))}
              </div>

              <div className="card">
                {fsActiveRows.length === 0 ? (
                  <p style={{ color: '#6b7280', fontStyle: 'italic', textAlign: 'center', padding: '24px 0' }}>No vehicles in this category.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>Registration</th>
                          <th>Site/Location</th>
                          <th>Value</th>
                          <th>Gross Vehicle Weight</th>
                          <th>Cover Status</th>
                          <th>Cover Type</th>
                          <th>{fleetReconciliation ? `Cover as of ${formatDateBritish(endDate)}` : 'Policy Start Date'}</th>
                          <th>Cover End Date</th>
                          <th style={{ textAlign: 'center' }}>Days on Cover</th>
                          <th>Approved By</th>
                          <th>Off Cover Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fsActiveRows.map((r, i) => (
                          <tr key={i}>
                            <td><strong>{r.clientName}</strong></td>
                            <td>{r.registration}</td>
                            <td>{r.siteLocation}</td>
                            <td>{r.value ? `\u00a3${r.value}` : '\u2014'}</td>
                            <td>{r.grossVehicleWeightKg != null ? `${r.grossVehicleWeightKg.toLocaleString()} kg` : '—'}</td>
                            <td>
                              <span style={{ color: statusColour(r.status), fontWeight: 600 }}>
                                {r.status === 'Active' ? 'On Cover' : r.status === 'Cover Pending' ? 'Cover Pending' : r.status || '\u2014'}
                              </span>
                            </td>
                            <td>{r.coverType || 'Permanent'}</td>
                            <td>{fleetReconciliation ? formatDateBritish(endDate) : r.inceptionDate ? formatDateBritish(r.inceptionDate) : '\u2014'}</td>
                            <td>{r.endDate        ? formatDateBritish(r.endDate)        : '\u2014'}</td>
                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>{calcDaysOnCover(r.inceptionDate, r.endDate)}</td>
                            <td>{r.onCoverBy ?? '\u2014'}</td>
                            <td>{r.offCoverDate ? formatDateBritish(r.offCoverDate) : '\u2014'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ──────────────── Custom Report tab ──────────────── */}
      {activeTab === 'customReport' && (
        <>
          {renderFilters()}

          {/* Category selection */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px' }}>Select Report Categories</h3>
            <div className="form-group" style={{ maxWidth: 520, marginBottom: 16 }}>
              <label htmlFor="exclude-requesters">Exclude requesters</label>
              <input id="exclude-requesters" value={excludedRequesters} onChange={(e) => setExcludedRequesters(e.target.value)} placeholder="e.g. Matt Murphy, Yourshield Team" />
              <small style={{ color: '#6b7280' }}>Comma-separated names. Matching additions, temporary additions, removals and vehicle changes will be excluded.</small>
            </div>
            <div style={{ marginBottom: '16px', padding: '10px 14px', background: '#f0f9ff', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
              onClick={toggleAll}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} onClick={(e) => e.stopPropagation()} style={{ width: 16, height: 16, cursor: 'pointer' }} />
              <strong style={{ fontSize: '14px' }}>Select All</strong>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px', marginTop: '12px' }}>
              {(Object.keys(categories) as (keyof CustomReportCategories)[]).map((key) => {
                const meta = CATEGORY_META[key];
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', border: `2px solid ${categories[key] ? meta.colour : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', background: categories[key] ? meta.colour + '0d' : '#fff', transition: 'all 0.15s' }}>
                    <input
                      type="checkbox"
                      checked={categories[key]}
                      onChange={() => setCategories((prev) => ({ ...prev, [key]: !prev[key] }))}
                      style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer', accentColor: meta.colour }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, color: '#111827', fontSize: '14px' }}>{meta.label}</div>
                      <div style={{ color: '#6b7280', fontSize: '12px', marginTop: 2 }}>{meta.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <button className="btn btn-primary" onClick={loadCustomReport} disabled={crLoading || noneSelected || !hasFilter}>
                {crLoading ? 'Loading...' : 'Generate Custom Report'}
              </button>
              {customData && totalCustomResults > 0 && (
                <button className="btn btn-secondary" onClick={exportCustomReportToExcel}>📥 Export to Excel</button>
              )}
            </div>
          </div>

          {/* Results */}
          {customData && (
            <>
              {renderCustomCategory('pendingCover', customData.pendingCover, (rows) => (
                <table>
                  <TableHeader cols={['Client', 'Registration', 'Make/Model', 'Type', 'Gross Vehicle Weight', 'Site', 'Value', 'Requested Date & Time', 'Requested By', 'Status']} />
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td><strong>{r.clientName}</strong></td>
                        <td>{r.registration}</td>
                        <td>{r.make} {r.model}</td>
                        <td>{r.vehicleType || '—'}</td>
                        <td>{r.grossVehicleWeightKg != null ? `${r.grossVehicleWeightKg.toLocaleString()} kg` : '—'}</td>
                        <td>{r.siteLocation}</td>
                        <td>£{r.vehicleValue}</td>
                        <td>{fmtDT(r.requestedAt)}</td>
                        <td>{r.requestedBy}</td>
                        <td><span style={{ color: statusColour(r.status), fontWeight: 600 }}>{r.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}

              {renderCustomCategory('onCover', customData.onCover, (rows) => (
                <table>
                  <TableHeader cols={['Client', 'Registration', 'Make/Model', 'Type', 'Gross Vehicle Weight', 'Site', 'Value', 'Approved Date & Time', 'Approved By', 'Policy Start Date', 'Cover Type', 'Status']} />
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td><strong>{r.clientName}</strong></td>
                        <td>{r.registration}</td>
                        <td>{r.make} {r.model}</td>
                        <td>{r.vehicleType || '\u2014'}</td>
                        <td>{r.grossVehicleWeightKg != null ? `${r.grossVehicleWeightKg.toLocaleString()} kg` : '—'}</td>
                        <td>{r.siteLocation}</td>
                        <td>£{r.vehicleValue}</td>
                        <td>{fmtDT(r.approvedAt)}</td>
                        <td>{r.approvedBy || '\u2014'}</td>
                        <td>{r.inceptionDate ? formatDateBritish(r.inceptionDate) : '\u2014'}</td>
                        <td>{r.isTempCover ? 'Temporary' : 'Permanent'}</td>
                        <td><span style={{ color: statusColour(r.status), fontWeight: 600 }}>{r.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}

              {renderCustomCategory('tempAdditions', customData.tempAdditions, (rows) => (
                <table>
                  <TableHeader cols={['Client', 'Registration', 'Make/Model', 'Type', 'Gross Vehicle Weight', 'Site', 'Value', 'Requested Date & Time', 'Requested By', 'Temp Cover Start', 'Temp Cover End', 'Status', 'Processed', 'Processed By']} />
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td><strong>{r.clientName}</strong></td>
                        <td>{r.registration}</td>
                        <td>{r.make} {r.model}</td>
                        <td>{r.vehicleType || '—'}</td>
                        <td>{r.grossVehicleWeightKg != null ? `${r.grossVehicleWeightKg.toLocaleString()} kg` : '—'}</td>
                        <td>{r.siteLocation}</td>
                        <td>£{r.vehicleValue}</td>
                        <td>{fmtDT(r.requestedAt)}</td>
                        <td>{r.requestedBy}</td>
                        <td>{r.coverStartDate ? formatDateBritish(r.coverStartDate) : '—'}</td>
                        <td>{r.coverEndDate   ? formatDateBritish(r.coverEndDate)   : '—'}</td>
                        <td><span style={{ color: statusColour(r.status), fontWeight: 600 }}>{r.status}</span></td>
                        <td>{r.processedAt ? fmtDT(r.processedAt) : '—'}</td>
                        <td>{r.processedBy || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}

              {renderCustomCategory('removalRequests', customData.removalRequests, (rows) => (
                <table>
                  <TableHeader cols={['Client', 'Registration', 'Make/Model', 'Type', 'Gross Vehicle Weight', 'Site', 'Requested Date & Time', 'Requested By', 'Req. Cover End Date', 'Status', 'Processed', 'Processed By']} />
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td><strong>{r.clientName}</strong></td>
                        <td>{r.registration}</td>
                        <td>{r.make} {r.model}</td>
                        <td>{r.vehicleType || '—'}</td>
                        <td>{r.grossVehicleWeightKg != null ? `${r.grossVehicleWeightKg.toLocaleString()} kg` : '—'}</td>
                        <td>{r.siteLocation}</td>
                        <td>{fmtDT(r.requestedAt)}</td>
                        <td>{r.requestedBy}</td>
                        <td>{r.requestedCoverEndDate ? formatDateBritish(r.requestedCoverEndDate) : '—'}</td>
                        <td><span style={{ color: statusColour(r.status), fontWeight: 600 }}>{r.status}</span></td>
                        <td>{r.processedAt ? fmtDT(r.processedAt) : '—'}</td>
                        <td>{r.processedBy || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}

              {renderCustomCategory('changeOfVehicle', customData.changeOfVehicle, (rows) => (
                <table>
                  <TableHeader cols={['Client', 'Old Reg', 'Old Make/Model', 'New Reg', 'New Make/Model', 'New Type', 'New Value', 'Site', 'Requested Date & Time', 'Requested By', 'Cover Start', 'Status', 'Processed', 'Processed By']} />
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td><strong>{r.clientName}</strong></td>
                        <td>{r.oldRegistration}</td>
                        <td>{r.oldMake} {r.oldModel}</td>
                        <td>{r.newRegistration}</td>
                        <td>{r.newMake} {r.newModel}</td>
                        <td>{r.newVehicleType || '—'}</td>
                        <td>£{r.newVehicleValue}</td>
                        <td>{r.siteLocation}</td>
                        <td>{fmtDT(r.requestedAt)}</td>
                        <td>{r.requestedBy}</td>
                        <td>{r.coverStartDate ? formatDateBritish(r.coverStartDate) : '—'}</td>
                        <td><span style={{ color: statusColour(r.status), fontWeight: 600 }}>{r.status}</span></td>
                        <td>{r.processedAt ? fmtDT(r.processedAt) : '—'}</td>
                        <td>{r.processedBy || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}

{totalCustomResults === 0 && (
                <div className="card">
                  <p style={{ color: '#6b7280', fontStyle: 'italic', textAlign: 'center', padding: '24px' }}>No records found for the selected categories and filters.</p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ──────────────── Scheduled Reports tab ──────────────── */}
      {activeTab === 'scheduledReports' && (
        <ScheduledReportsTab />
      )}

    </div>
  );
};

export default InsightsReporting;
